import { AllShapes, Point, ShapeTypes } from '../types'
import { v4 as uuidv4 } from 'uuid'
import { SkiaPolygon } from '../SkiaPolygon'
import { SkiaOval } from '../SkiaOval'
import { SkiaRect } from '../SkiaRect'
import { plainToInstance } from 'class-transformer'
import { Pin } from '../Pin'

export type Mode = 'EDIT' | 'VIEW' | 'SELECT'
export const DEFAULT_COLOR = '#9CA3AF'
export const ACTIVE_COLOR = '#1976D2'
export const INITIAL_COLOR = '#6454C0'
export const CREATE_ZONES_COLOR = '#1976D2'

export class CanvasState {
  mode: Mode
  shapes: AllShapes[]
  pins: Pin[] = []
  multipleSelection: boolean = false
  centerPoint: Point = { x: 0, y: 0 }
  canvasBounds: { width: number; height: number } = { width: 0, height: 0 }
  selectedPermitId: number | null = null

  public static new(payload: Partial<CanvasState>): CanvasState {
    const entity = plainToInstance(CanvasState, payload)

    return entity
  }

  updateState(newShapes: AllShapes[]): CanvasState {
    const shapes = newShapes.map((shape) => {
      const oldShape = this.shapes.find((s) => s.id === shape.id)
      if (oldShape) {
        shape.isSelected = oldShape.isSelected
        shape.isEditing = oldShape.isEditing
        shape.isNew = oldShape.isNew
      }
      return shape
    })

    return CanvasState.new({
      mode: this.mode,
      shapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public getSelectedShapes(): AllShapes[] {
    const selectedShapes = this.shapes.filter((shape) => shape.isSelected)

    if (this.mode === 'EDIT') return selectedShapes

    // If any shape in a group is selected, include all shapes in that group
    const selectedGroupIds = new Set(
      selectedShapes.map((s) => s.groupId).filter(Boolean),
    )
    if (selectedGroupIds.size > 0) {
      return this.shapes.filter(
        (s) => s.isSelected || (s.groupId && selectedGroupIds.has(s.groupId)),
      )
    }

    return selectedShapes
  }

  public getSelectedGroupIdUnderEdit(): string | null {
    const selectedShapes = this.getSelectedShapes()
    return selectedShapes.find((shape) => shape.isEditing)?.groupId || null
  }

  public getLastSelectedShape(): AllShapes | null {
    const selectedShapes = this.getSelectedShapes()
    return selectedShapes[selectedShapes.length - 1]
  }

  public getShapesUnderEdit(): AllShapes[] {
    return this.shapes.filter((shape) => shape.isEditing)
  }

  public getGroupsWithShapesUnderEdit(): {
    groupId: string
    shapes: AllShapes[]
  }[] {
    const shapesUnderEdit = this.shapes.filter(
      (shape) => shape.isEditing || shape.isSelected,
    )
    const groupedShapes = new Map<string, AllShapes[]>()

    shapesUnderEdit.forEach((shape) => {
      if (!shape.groupId) return

      if (!groupedShapes.has(shape.groupId)) {
        groupedShapes.set(shape.groupId, [])
      }
      groupedShapes.get(shape.groupId).push(shape)
    })

    return Array.from(groupedShapes.entries()).map(([groupId, shapes]) => ({
      groupId,
      shapes,
    }))
  }

  public resetToViewMode(oldShapes?: AllShapes[]): CanvasState {
    const selectedShapes = this.shapes.filter(
      (shape) => shape.isSelected || shape.isEditing,
    )
    const selectedShapeIds = new Set(selectedShapes.map((shape) => shape.id))

    const updatedShapes = this.shapes.map((shape) => {
      shape.isEditing = false
      shape.isNew = false
      if (!selectedShapeIds.has(shape.id)) {
        return shape
      }

      const oldShape = oldShapes?.find((s) => s.id === shape.id)
      if (oldShape) {
        shape = oldShape
        shape.isSelected = true
        return shape
      }

      shape.isSelected = true
      return shape
    })

    return CanvasState.new({
      mode: 'VIEW',
      shapes: updatedShapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public changeSelectedShapeColor(newColor: string): CanvasState {
    const selectedShapes = this.getSelectedShapes()
    const shapesWithNewColor = this.shapes.map((shape) => {
      if (selectedShapes.includes(shape)) {
        shape.color = newColor
      }
      return shape
    })
    const newState = CanvasState.new({
      mode: this.mode,
      shapes: shapesWithNewColor,
      pins: this.pins,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
    return newState
  }

  public editSelectedShapes(oldShapes?: AllShapes[]): CanvasState {
    const selectedShapes = this.getSelectedShapes()
    selectedShapes.forEach((shape) => {
      const oldShape = oldShapes?.find((s) => s.id === shape.id)
      if (oldShape) {
        shape = oldShape
      }

      shape.isSelected = false
      shape.isEditing = true
    })
    return CanvasState.new({
      mode: 'EDIT',
      shapes: this.shapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public selectEditedShapes(oldShapes?: AllShapes[]): CanvasState {
    const shapesUnderEdit = this.getShapesUnderEdit()
    const editingShapeIds = new Set(shapesUnderEdit.map((shape) => shape.id))

    if (oldShapes) {
      // Filter out new shapes that aren't in oldShapes
      this.shapes = this.shapes
        .filter(
          (shape) =>
            !editingShapeIds.has(shape.id) || // Keep shapes that aren't being edited
            oldShapes.some((oldShape) => oldShape.id === shape.id), // Or shapes that exist in oldShapes
        )
        .map((shape) => {
          if (editingShapeIds.has(shape.id)) {
            // If it's an edited shape, restore it from oldShapes
            const oldShape = oldShapes.find((s) => s.id === shape.id)
            return oldShape || shape
          }
          return shape
        })
    }

    // Update states for shapes that were under edit
    this.shapes.forEach((shape) => {
      if (editingShapeIds.has(shape.id)) {
        shape.isSelected = true
        shape.isEditing = false
        shape.isNew = false
      }
    })

    return CanvasState.new({
      mode: 'SELECT',
      shapes: this.shapes,
      pins: this.pins,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public getNewShape(): AllShapes | null {
    return this.shapes.find((shape) => shape.isBeingCreated) || null
  }

  public getSelectedGroup(): string | null {
    return (
      this.shapes.find((shape) => shape.isSelected || shape.isEditing)
        ?.groupId || null
    )
  }

  public getSelectedGroups(): string[] {
    const selectedGroupIds = new Set(
      this.shapes
        .filter((shape) => shape.isSelected || shape.isEditing)
        .map((shape) => shape.groupId)
        .filter(Boolean),
    )
    return Array.from(selectedGroupIds)
  }

  public areAllShapesSelected(): boolean {
    return this.shapes.every((shape) => shape.isSelected)
  }

  public cancelNewItem(): CanvasState {
    const filteredNewItems = this.shapes.filter((shape) => !shape.isNew)

    return CanvasState.new({
      mode: 'VIEW',
      shapes: filteredNewItems,
      pins: this.pins,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public createNewShape(
    type: ShapeTypes,
    color?: string,
    name?: string,
    groupId?: string | null,
  ): AllShapes {
    const point1 = { x: 0, y: 0 }
    const point2 = { x: 0, y: 0 }
    // Deselect all shapes before creating new shape
    this.shapes.forEach((shape) => {
      shape.isSelected = false
    })

    let shape: AllShapes
    switch (type) {
      case 'POLYGON':
        shape = SkiaPolygon.new({
          id: uuidv4(),
          type: 'POLYGON',
          points: [],
          color,
          name,
          groupId,
        })
        break

      case 'RECT':
        shape = SkiaRect.new({
          id: uuidv4(),
          type: 'RECT',
          x: Math.min(point1.x, point2.x),
          y: Math.min(point1.y, point2.y),
          width: Math.abs(point1.x - point2.x),
          height: Math.abs(point1.y - point2.y),
          color,
          name,
          groupId,
        })
        break

      case 'OVAL':
        shape = SkiaOval.new({
          id: uuidv4(),
          type: 'OVAL',
          x: Math.min(point1.x, point2.x),
          y: Math.min(point1.y, point2.y),
          width: Math.abs(point1.x - point2.x),
          height: Math.abs(point1.y - point2.y),
          color,
          name,
          groupId,
        })
        break

      default:
        throw new Error(`Unsupported shape type: ${type}`)
    }

    shape.isBeingCreated = true
    shape.isSelected = true
    shape.isEditing = true
    shape.isNew = true
    return shape
  }

  public createPin(): CanvasState {
    const pin = Pin.new({
      uuid: uuidv4(),
      x: 0,
      y: 0,
    })

    pin.isBeingCreated = true
    pin.isNew = true

    this.pins.push(pin)

    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      centerPoint: this.centerPoint,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public isPinInCanvas(point: Point): boolean {
    return (
      point.x >= 0 &&
      point.x <= this.canvasBounds.width &&
      point.y >= 0 &&
      point.y <= this.canvasBounds.height
    )
  }

  public setCenterPoint(point: Point): CanvasState {
    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      centerPoint: point,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public finalizePinCreation(point?: Point): CanvasState {
    const centerPoint = point || this.centerPoint
    const pin = this.pins.find((pin) => pin.isBeingCreated)
    if (!pin) return this

    const pointInShapes = this.shapes.filter((shape) =>
      shape.isInBounds(centerPoint),
    )

    pin.isBeingCreated = false
    pin.x = centerPoint.x
    pin.y = centerPoint.y

    if (pointInShapes.length > 0) {
      pin.areaUuids = pointInShapes.map((shape) => shape.id)
      pointInShapes.forEach((shape) => {
        shape.isActive = true
        shape.isInitial = true
      })
    }

    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      centerPoint: this.centerPoint,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public cancelPinCreation(): CanvasState {
    this.pins = this.pins.filter((pin) => !pin.isBeingCreated)
    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      centerPoint: this.centerPoint,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public isPinBeingCreated(): boolean {
    return this.pins.some((pin) => pin.isBeingCreated)
  }

  public getNewPins(): Pin[] {
    return this.pins.filter((pin) => pin.isNew && pin.x !== 0 && pin.y !== 0)
  }

  public getSelectedPin(): Pin | null {
    return this.pins.find((pin) => pin.isSelected) || null
  }

  public undoLastPin(): CanvasState {
    const lastPin = this.pins.pop()

    const lastPinShape = this.shapes.find((shape) =>
      lastPin?.areaUuids?.includes(shape.id),
    )
    const lastPinShapeHasNewPin = this.pins.some(
      (pin) => pin.areaUuids?.includes(lastPinShape?.id) && pin.isNew,
    )
    const hasPinsInShape = this.pins.some((pin) =>
      pin.areaUuids?.includes(lastPinShape?.id),
    )

    if (lastPinShape && !lastPinShapeHasNewPin) {
      lastPinShape.isInitial = false
    }

    if (!hasPinsInShape && lastPinShape) {
      lastPinShape.isActive = false
    }

    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      centerPoint: this.centerPoint,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public deletePin(pin: Pin): CanvasState {
    this.pins = this.pins.filter((p) => p.uuid !== pin.uuid)

    const pinShape = this.shapes.find((shape) =>
      pin.areaUuids?.includes(shape.id),
    )
    const pinShapeHasNewPin = this.pins.some(
      (p) => p.areaUuids?.includes(pinShape?.id) && p.isNew,
    )
    const hasPinsInShape = this.pins.some((p) =>
      p.areaUuids?.includes(pinShape?.id),
    )

    if (pinShape && !pinShapeHasNewPin) {
      pinShape.isInitial = false
    }

    if (!hasPinsInShape && pinShape) {
      pinShape.isActive = false
    }

    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      centerPoint: this.centerPoint,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public getShapesWithNewPins(): AllShapes[] {
    return this.shapes.filter((shape) =>
      this.pins.some((pin) => pin.areaUuids?.includes(shape.id) && pin.isNew),
    )
  }

  public finalizeNewShape() {
    this.shapes = this.shapes.map((shape) => {
      shape.isBeingCreated = false
      return shape
    })
  }

  public selectAllShapes(): CanvasState {
    const shouldSelect = this.getSelectedShapes().length !== this.shapes.length
    const newShapes = this.shapes.map((s) => {
      s.isSelected = shouldSelect
      return s
    })
    return CanvasState.new({
      mode: this.mode,
      shapes: newShapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public deselectAllShapes(): CanvasState {
    const newShapes = this.shapes.map((s) => {
      s.isSelected = false
      return s
    })
    return CanvasState.new({
      mode: this.mode,
      shapes: newShapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public selectPin(pin: Pin, readOnly?: boolean): CanvasState {
    if (!pin) {
      // Deselect all pins if no pin is provided
      this.pins.forEach((p) => {
        p.isSelected = false
      })

      return CanvasState.new({
        mode: this.mode,
        shapes: this.shapes,
        pins: this.pins,
        multipleSelection: this.multipleSelection,
        centerPoint: this.centerPoint,
        canvasBounds: this.canvasBounds,
        selectedPermitId: this.selectedPermitId,
      })
    }

    const pinsToSelect =
      pin.pinsInGroupIds?.map((pinUuid) =>
        this.pins.find((p) => p.uuid === pinUuid),
      ) ?? []

    const newPins = this.pins.map((p) => {
      p.isSelected =
        (pinsToSelect.some((pin) => pin.uuid === p.uuid) ||
          (p.uuid === pin.uuid && !readOnly)) &&
        !p.isSelected
      return p
    })

    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: newPins,
      multipleSelection: this.multipleSelection,
      centerPoint: this.centerPoint,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public selectPins(pins: Pin[]): CanvasState {
    const newPins = this.pins.map((p) => {
      p.isSelected = pins.some((pin) => pin.uuid === p.uuid)
      return p
    })
    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: newPins,
      multipleSelection: this.multipleSelection,
      centerPoint: this.centerPoint,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public selectShape(
    shape: AllShapes | null,
    multiSelect: boolean = false,
    readOnly: boolean = false,
  ): CanvasState {
    if (readOnly)
      return CanvasState.new({
        mode: this.mode,
        shapes: this.shapes,
        pins: this.pins,
        multipleSelection: this.multipleSelection,
        centerPoint: this.centerPoint,
        canvasBounds: this.canvasBounds,
        selectedPermitId: this.selectedPermitId,
      })

    if (!shape) {
      // Deselect all shapes if no shape is provided
      this.shapes.forEach((s) => {
        s.isSelected = false
      })
      return CanvasState.new({
        mode: this.mode,
        shapes: this.shapes,
        pins: this.pins,
        multipleSelection: this.multipleSelection,
        centerPoint: this.centerPoint,
        canvasBounds: this.canvasBounds,
        selectedPermitId: this.selectedPermitId,
      })
    }

    // In EDIT mode, select shapes individually
    if (this.mode === 'EDIT') {
      this.shapes.forEach((s) => {
        if (s.id === shape.id) {
          s.isSelected = !s.isSelected // Toggle selection
        } else if (!multiSelect) {
          s.isSelected = false
        }
      })
      return CanvasState.new({
        mode: this.mode,
        shapes: this.shapes,
        pins: this.pins,
        multipleSelection: this.multipleSelection,
        canvasBounds: this.canvasBounds,
        selectedPermitId: this.selectedPermitId,
      })
    }

    // In other modes, handle group selection
    const groupShapes = shape.groupId
      ? this.shapes.filter((s) => s.groupId === shape.groupId)
      : [shape]

    this.shapes.forEach((s) => {
      if (groupShapes.some((gs) => gs.id === s.id)) {
        s.isSelected = !s.isSelected // Toggle selection
      } else if (!multiSelect) {
        s.isSelected = false
      }
    })
    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      centerPoint: this.centerPoint,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public deleteShape(id: string): CanvasState {
    this.shapes = this.shapes.filter((s) => s.id !== id)

    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: this.pins,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public updateShape(shape: AllShapes): CanvasState {
    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes.map((s) => (s.id === shape.id ? shape : s)),
      pins: this.pins,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public deselectShape(shape: AllShapes): CanvasState {
    shape.isBeingCreated = false
    shape.isSelected = false
    return CanvasState.new({
      mode: this.mode,
      shapes: [...this.shapes],
      pins: this.pins,
      centerPoint: this.centerPoint,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public undoPolygon(id: string): CanvasState {
    const shape = this.shapes.find((s) => s.id === id)
    if (shape instanceof SkiaPolygon) {
      shape.points.pop()
    }
    return CanvasState.new({
      mode: this.mode,
      shapes: [...this.shapes],
      pins: this.pins,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public handlePolygonPoint(
    translatedPoint: Point,
    closeDistance: number,
  ): CanvasState {
    const newShape = this.getNewShape()
    if (!(newShape instanceof SkiaPolygon)) return this

    if (newShape.isBeingCreated && newShape.points.length > 0) {
      // Check if clicking near the first point to close the polygon
      const firstPoint = newShape.points[0]
      const dx = firstPoint.x - translatedPoint.x
      const dy = firstPoint.y - translatedPoint.y

      if (
        newShape.points.length > 2 &&
        Math.sqrt(dx * dx + dy * dy) <= closeDistance
      ) {
        // Close the polygon
        newShape.isBeingCreated = false
        newShape.points.push({ ...firstPoint }) // Add first point to close
      } else {
        // Add new point to existing polygon
        newShape.addPoint(translatedPoint)
      }
    } else {
      // Start new polygon
      this.shapes.forEach((s) => (s.isSelected = false))
      newShape.addPoint(translatedPoint)
    }

    return CanvasState.new({
      mode: this.mode,
      shapes: [...this.shapes],
      pins: this.pins,
      selectedPermitId: this.selectedPermitId,
    })
  }

  public updateLabels(
    newLabels: { name: string; enabled: boolean }[],
  ): CanvasState {
    // Update labels for each shape with a groupId
    this.shapes = this.shapes.map((shape) => {
      if (shape.groupId && shape.labels) {
        shape.labels = shape.labels.map((label) => ({
          ...label,
          enabled: !!newLabels.find((l) => l.name === label.name)?.enabled,
        }))
      }
      return shape
    })

    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: this.pins,
      centerPoint: this.centerPoint,
      canvasBounds: this.canvasBounds,
      selectedPermitId: this.selectedPermitId,
    })
  }

  /**
   * Select a permit by ID. Passing null deselects the current permit.
   * Used for analytics tracking in kiosk mode.
   */
  public selectPermit(permitId: number | null): CanvasState {
    return CanvasState.new({
      mode: this.mode,
      shapes: this.shapes,
      pins: this.pins,
      multipleSelection: this.multipleSelection,
      centerPoint: this.centerPoint,
      canvasBounds: this.canvasBounds,
      selectedPermitId: permitId,
    })
  }

  /**
   * Get selected zone IDs (groupIds) for URL params.
   * Returns only zones that are currently selected by the user.
   */
  public getSelectedZoneIds(): string[] {
    return this.shapes
      .filter((shape) => shape.isSelected)
      .map((shape) => shape.groupId)
      .filter(Boolean) as string[]
  }

  /**
   * Get URL query params for analytics tracking.
   * Derives permit from selected pin - purely observational, doesn't modify state.
   */
  public getUrlParams(): { selectedZones?: string; selectedPermit?: string } {
    const zoneIds = this.getSelectedZoneIds()
    const params: { selectedZones?: string; selectedPermit?: string } = {}

    if (zoneIds.length > 0) {
      params.selectedZones = zoneIds.join(',')
    }

    // Derive permit from the selected pin's permitId
    const selectedPin = this.getSelectedPin()
    const permitId = selectedPin?.permitId

    if (permitId) {
      params.selectedPermit = String(permitId)
    }

    return params
  }
}
